import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { DataTable, type DataColumn } from '@/components/ui/DataTable'
import { TableSkeleton } from '@/components/ui/Skeleton'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { fmtMoney, fmtPct, fmtRate } from '@/lib/format'

export interface DistributionShare {
  id: number
  partner_id: number
  partner_name: string
  share_pct: string
  amount_rub: string
  paid_at: string | null
  paid_ref: string | null
}

export interface Distribution {
  id: number
  period_start: string
  period_end: string
  revenue_rub: string
  provider_cost_rub: string
  referral_payouts_rub: string
  /** provider fees on the period's payments and the network fee of partner payouts */
  payment_fees_rub?: string
  payout_fees_rub?: string
  expenses_rub: string
  profit_rub: string
  distributed_pct: string
  distributed_rub: string
  retained_rub: string
  rate_usdt_rub: string
  status: 'draft' | 'approved'
  note: string | null
  created_at: string | null
  approved_at: string | null
  shares: DistributionShare[]
}

export interface DistributionTableProps {
  items?: Distribution[]
  /** owner only: approve / discard drafts, mark shares paid */
  canEdit: boolean
  busy?: boolean
  onApprove?: (id: number) => void
  onDelete?: (id: number) => void
  onMarkPaid?: (share: DistributionShare) => void
}

/** Rubles with kopecks, grouped («1 490,00»); the caller adds «₽». Exact (lib/format, WP-04). */
export const rub = (v: string | number) => fmtMoney(v)
const day = (iso: string) => new Date(iso).toLocaleDateString('ru-RU')

/** The shares of one period: who, which share, how much, paid or not; the owner marks a payout
 *  of an approved period (a plain secondary action — nothing is taken back here). */
function shareColumns(
  d: Distribution,
  canEdit: boolean,
  busy: boolean | undefined,
  onMarkPaid: ((share: DistributionShare) => void) | undefined,
): DataColumn<DistributionShare>[] {
  const cols: DataColumn<DistributionShare>[] = [
    { id: 'partner', header: 'Участник', type: 'name', cell: (sh) => sh.partner_name, mobile: 'title' },
    {
      id: 'share',
      header: 'Доля',
      type: 'percent',
      className: 'text-ink-2',
      cell: (sh) => fmtPct(sh.share_pct, { digits: 0, max: 3 }),
      mobile: 'field',
    },
    { id: 'amount', header: 'Сумма, ₽', unit: '₽', type: 'money', cell: (sh) => rub(sh.amount_rub), mobile: 'amount' },
    {
      id: 'paid',
      header: 'Выплата',
      type: 'status',
      mobile: 'status',
      cell: (sh) =>
        sh.paid_at ? (
          <span className="inline-flex flex-col items-start gap-1">
            <span className="inline-flex items-center gap-2">
              <StatusBadge tone="ok">Выплачено</StatusBadge>
              <span className="tnum text-caption text-ink-3">{day(sh.paid_at)}</span>
            </span>
            {sh.paid_ref && <span className="text-caption text-ink-3">{sh.paid_ref}</span>}
          </span>
        ) : (
          <StatusBadge tone="neutral">Не выплачено</StatusBadge>
        ),
    },
  ]
  if (canEdit) {
    cols.push({
      id: 'actions',
      header: '',
      type: 'actions',
      cell: (sh) =>
        d.status === 'approved' && !sh.paid_at && Number(sh.amount_rub) > 0 ? (
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => onMarkPaid?.(sh)}>
            Отметить выплату
          </Button>
        ) : null,
    })
  }
  return cols
}

/** Бухгалтерия → Распределения: one card per period — the frozen P&L, the shares and
 *  their payout state. Presentational; every state is in Storybook (rule 22). */
export function DistributionTable({ items, canEdit, busy, onApprove, onDelete, onMarkPaid }: DistributionTableProps) {
  if (items === undefined)
    return (
      <Card className="p-0">
        <TableSkeleton rows={4} cols={4} />
      </Card>
    )
  if (items.length === 0)
    return (
      <Card>
        <p className="text-center text-body text-ink-3">
          Распределений пока нет{canEdit ? ' — выберите период и нажмите «Рассчитать»' : ''}
        </p>
      </Card>
    )
  return (
    <div className="flex flex-col gap-4">
      {items.map((d) => {
        const negative = Number(d.profit_rub) <= 0
        return (
          <Card key={d.id} className="p-0">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-hairline p-3">
              {/* wraps: the badges are nowrap (§6.5), period + «Черновик» + «Убыток — долей нет» is
                  wider than a phone card */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="text-body-lg text-ink-1">
                  {day(d.period_start)} — {day(d.period_end)}
                </span>
                {d.status === 'approved' ? (
                  <StatusBadge tone="ok">Утверждено</StatusBadge>
                ) : (
                  <StatusBadge tone="neutral">Черновик</StatusBadge>
                )}
                {negative && <StatusBadge tone="warn">Убыток — долей нет</StatusBadge>}
              </div>
              {canEdit && d.status === 'draft' && (
                <div className="flex gap-2">
                  <Button size="sm" variant="primary" loading={busy} onClick={() => onApprove?.(d.id)}>
                    Утвердить
                  </Button>
                  <Button size="sm" variant="destructive" disabled={busy} onClick={() => onDelete?.(d.id)}>
                    Удалить
                  </Button>
                </div>
              )}
            </div>
            {/* one column on a phone: a label and a figure like «−3 102 000,00» need the full
                width (two columns ran the figures into the next label and past the card) */}
            <dl className="grid grid-cols-1 gap-x-6 gap-y-2 p-3 text-body sm:grid-cols-2 md:grid-cols-3">
              <Row k="Выручка" v={d.revenue_rub} />
              <Row k="Комиссии платёжных провайдеров" v={`−${rub(d.payment_fees_rub ?? 0)}`} raw />
              <Row k="Провайдеры" v={`−${rub(d.provider_cost_rub)}`} raw />
              <Row k="Партнёрские выплаты" v={`−${rub(d.referral_payouts_rub)}`} raw />
              <Row k="Комиссия сети за выплаты" v={`−${rub(d.payout_fees_rub ?? 0)}`} raw />
              <Row k="Расходы" v={`−${rub(d.expenses_rub)}`} raw />
              <Row k="Прибыль" v={d.profit_rub} strong />
              <Row k={`Распределено (${fmtPct(d.distributed_pct, { digits: 0, max: 3 })})`} v={d.distributed_rub} />
              <Row k="Остаётся в компании" v={d.retained_rub} />
              <Row k="Курс USDT/RUB" v={fmtRate(d.rate_usdt_rub)} raw />
            </dl>
            <div className="border-t border-hairline p-3">
              {d.shares.length === 0 ? (
                <p className="text-caption text-ink-3">Активных участников на момент расчёта не было</p>
              ) : (
                <DataTable
                  label={`Доли участников за ${day(d.period_start)} — ${day(d.period_end)}`}
                  rows={d.shares}
                  columns={shareColumns(d, canEdit, busy, onMarkPaid)}
                  getRowId={(sh) => String(sh.id)}
                />
              )}
            </div>
            {d.note && <p className="border-t border-hairline p-3 text-caption text-ink-3">{d.note}</p>}
          </Card>
        )
      })}
    </div>
  )
}

function Row({ k, v, strong, raw }: { k: string; v: string; strong?: boolean; raw?: boolean }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-ink-2">{k}</dt>
      <dd className={`tnum text-right ${strong ? 'text-ink-1 font-medium' : 'text-ink-1'}`}>{raw ? v : rub(v)}</dd>
    </div>
  )
}
